import { MooringDrawingSetup, MooringServiceCorridorDirection } from 'models/mooring';
import { Coordinate } from 'ol/coordinate';
import { ensureNumberOrDefault } from 'lib/helpers';

/**
 * Converts a circumference to a radius.
 * @param circumference Circumference of a circle in meters.
 * @description Converts a circumference to a radius.
 * @returns the radius of the circle in meters.
 */
export function circumferenceToRadius(circumference: number) {
  return (circumference || 0) / (2 * Math.PI);
}

/**
 * Returns the given angel in degrees as radians.
 * Optionally provide an offset
 */
export function angleToRadians(angleDegrees: number, angleOffset = 0) {
  return ((angleDegrees + angleOffset) * Math.PI) / 180;
}

/**
 * Returns the spread angle for N entries, for given N.
 * This is to get an angle offset based on the Nth entry.
 * For three point that would normally be rendered at the same location, this will return
 * the spread for the points so that they can be rendered away from each other.
 * E.g. for three lines that starts and ends at the same position. This can be used to
 * get a spred at the end, so that the first line gets -15 degrees, the second with 0 degrees, and last +15 degrees.
 */
export function getSpreadAngleForN(n: number, entries: number, spread = 6) {
  if (entries <= 1) return 0;
  return ((n - Math.floor(entries / 2)) / (entries - 1)) * (spread * entries);
}

/**
 * Converts a DMM degree string to decimal degrees.
 * Supports optional direction letters N/S/E/W (case-insensitive).
 * W and S produce negative values. If no direction letter is present, E/N (positive) is assumed.
 * Examples: "65°05.302'N", "23°38.258'W", "6°30.500"
 */
export function convertToDecimalDegrees(coord: string): number {
  if (!coord) return 0;
  coord = coord.replaceAll(',', '.');
  const regex = /(\d+)°(\d+(?:\.\d+)?)/;
  const match = coord.match(regex);

  if (!match) {
    return 0;
  }

  const degrees = parseFloat(match[1]);
  const minutes = parseFloat(match[2]);
  const decimalDegrees = degrees + minutes / 60;

  const upper = coord.toUpperCase();
  if (upper.includes('W') || upper.includes('S')) {
    return -decimalDegrees;
  }

  return decimalDegrees;
}

/**
 * Calculate simple angle between two points (for shorter distances)
 * This method doesn't account for Earth's curvature
 * @param lat1 - Latitude of first point in decimal degrees
 * @param lon1 - Longitude of first point in decimal degrees
 * @param lat2 - Latitude of second point in decimal degrees
 * @param lon2 - Longitude of second point in decimal degrees
 * @returns Rotation angle in degrees
 */
export function calculateSimpleAngle(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const deltaX = lat2 - lat1;
  const deltaY = lon2 - lon1;

  // Calculate angle in degrees
  const angleRadians = Math.atan2(deltaY, deltaX);
  const angleDegrees = (angleRadians * 180) / Math.PI;

  return angleDegrees;
}

export function DMMLonLatToDecimalLonLat(lon: string, lat: string): Coordinate {
  return [convertToDecimalDegrees(lon), convertToDecimalDegrees(lat)] as const;
}

/**
 * Calculates the change in latitude and longitude (in degrees) for a given distance in meters,
 * based on the Earth's radius and the latitude of the location.
 *
 * @param meters - The distance in meters to convert to latitude/longitude change.
 * @param latPos - The latitude (in decimal degrees) at which the calculation should be based.
 * @returns An array with the change in longitude and latitude ([deltaLon, deltaLat]).
 */
export function calculateLatLonOffsetFromMeters(meters: number, latPos: number) {
  const earthRadius = 6378137; // Earth radius in meters
  const deltaLat = (meters / 2 / earthRadius) * (180 / Math.PI);
  const deltaLon = (meters / 2 / (earthRadius * Math.cos(latPos * (Math.PI / 180)))) * (180 / Math.PI);

  return [deltaLon, deltaLat];
}

export function calculateScaledLatLonOffset(meters: number, latPos: number): [number, number] {
  const earthRadius = 6378137; // Earth radius in meters

  // Convert meters to degrees for latitude
  const deltaLat = (meters / earthRadius) * (180 / Math.PI);

  // Convert meters to degrees for longitude
  let deltaLon = (meters / (earthRadius * Math.cos(latPos * (Math.PI / 180)))) * (180 / Math.PI);

  // Apply a scaling factor to longitude to compensate for stretching
  const scaleFactor = Math.cos(latPos * (Math.PI / 180));
  deltaLon *= scaleFactor; // Scale down longitude adjustments at higher latitudes

  return [deltaLon, deltaLat];
}

/**
 * Rotates a point around a center (Clockwise)
 * @param point The point to rotate [lon, lat].
 * @param center The center point [lon, lat].
 * @param angleDeg Rotation angle in degrees.
 * @returns The new rotated point [lon, lat].
 */
export function rotatePoint(point: Coordinate, center: Coordinate, angleDeg: number): [number, number] {
  const angleRad = -angleDeg * (Math.PI / 180); // Convert to radians
  const cosTheta = Math.cos(angleRad);
  const sinTheta = Math.sin(angleRad);

  // Adjust longitude scaling based on latitude
  const scaleX = Math.cos(center[1] * (Math.PI / 180));

  // Translate point relative to center
  const x = (point[0] - center[0]) * scaleX;
  const y = point[1] - center[1];

  // Apply rotation matrix
  const rotatedX = (x * cosTheta - y * sinTheta) / scaleX + center[0];
  const rotatedY = x * sinTheta + y * cosTheta + center[1];

  return [rotatedX, rotatedY];
}

/**
 * Helper function to interpolate between two coordinates in a linear fashion.
 * The coordiantes should be in decimal degrees format [lon, lat].
 */
export function interpolateDecimalDegreesCoordinates(coord1: Coordinate, coord2: Coordinate, progress: number): Coordinate {
  const lon = coord1[0] + (coord2[0] - coord1[0]) * progress;
  const lat = coord1[1] + (coord2[1] - coord1[1]) * progress;
  return [lon, lat];
}

/**
 * Returns the corner points of a mooring drawing setup.
 * The points are returned in the order: NW, NE, SE, SW.
 * Each point is an array of [longitude, latitude] in decimal degrees.
 */
export const getCornerPoints = (data: MooringDrawingSetup, center: Coordinate, cagesX: number, cagesY: number) => {
  if (
    !data.NWPositionLat ||
    !data.NWPositionLong ||
    !data.NEPositionLat ||
    !data.NEPositionLong ||
    !data.SEPositionLat ||
    !data.SEPositionLong ||
    !data.SWPositionLat ||
    !data.SWPositionLong
  ) {
    let heightMeters = cagesY * data.GridLineLength || 0;
    let widthMeters = cagesX * data.GridLineLength || 0;

    if (data.ServiceCorridor) {
      const direction = data.ServiceCorridor.Direction;
      const serviceCorridorLength = data.ServiceCorridor.Width;
      if (direction === MooringServiceCorridorDirection.Horizontal) {
        heightMeters -= data.GridLineLength;
        heightMeters += ensureNumberOrDefault(serviceCorridorLength, data.GridLineLength);
      } else if (direction === MooringServiceCorridorDirection.Vertical) {
        widthMeters -= data.GridLineLength;
        widthMeters += ensureNumberOrDefault(serviceCorridorLength, data.GridLineLength);
      }
    }
    // If any corner is missing, calculate from center point
    const pos = calculateCornersFromCenter(center[0], center[1], widthMeters, heightMeters);
    let corners = [pos.NW, pos.NE, pos.SE, pos.SW];

    // Apply rotation if SiteAngleFromNorth is provided
    if (data.SiteAngleFromNorth !== null && data.SiteAngleFromNorth !== undefined) {
      corners = corners.map((corner) => rotatePoint(corner as Coordinate, center, data.SiteAngleFromNorth));
    }

    return corners;
  }

  const nw = [convertToDecimalDegrees(data.NWPositionLong), convertToDecimalDegrees(data.NWPositionLat)];
  const ne = [convertToDecimalDegrees(data.NEPositionLong), convertToDecimalDegrees(data.NEPositionLat)];
  const se = [convertToDecimalDegrees(data.SEPositionLong), convertToDecimalDegrees(data.SEPositionLat)];
  const sw = [convertToDecimalDegrees(data.SWPositionLong), convertToDecimalDegrees(data.SWPositionLat)];
  return [nw, ne, se, sw];
};

/**
 * Calculate a GPS point at a given meter distance along a line between two coordinates.
 * Assumes flat earth (valid for distances < 1000m).
 *
 * @param {number[]} startCoord - Starting coordinate [longitude, latitude] in decimal degrees
 * @param {number[]} endCoord - Ending coordinate [longitude, latitude] in decimal degrees
 * @param {number} meters - Distance in meters from start point
 * @returns {number[]} - Coordinate [longitude, latitude] at the specified distance
 */
export function getPointFromMeters(startCoord: Coordinate, endCoord: Coordinate, meters: number) {
  const [lon1, lat1] = startCoord;
  const [lon2, lat2] = endCoord;

  // Conversion factors (approximate, varies with latitude)
  const avgLat = (lat1 + lat2) / 2;
  const latRad = (avgLat * Math.PI) / 180;

  const metersPerDegreeLat = 111320;
  const metersPerDegreeLon = 111320 * Math.cos(latRad);

  // Convert coordinates to meters (relative to start)
  const dx = (lon2 - lon1) * metersPerDegreeLon;
  const dy = (lat2 - lat1) * metersPerDegreeLat;

  // Total distance between points
  const totalDistance = Math.sqrt(dx * dx + dy * dy);

  if (totalDistance === 0) {
    return [...startCoord];
  }

  // Calculate the ratio of desired distance to total distance
  const ratio = meters / totalDistance;

  // Calculate new coordinate
  const newLon = lon1 + (lon2 - lon1) * ratio;
  const newLat = lat1 + (lat2 - lat1) * ratio;

  return [newLon, newLat] as Coordinate;
}

/**
 * Generate a grid of points with variable spacing in X and Y directions.
 *
 * @param {number[]} topLeft - [longitude, latitude]
 * @param {number[]} topRight - [longitude, latitude]
 * @param {number[]} bottomLeft - [longitude, latitude]
 * @param {number[]} bottomRight - [longitude, latitude]
 * @param {number[]} xDistances - Array of distances in meters for X direction (columns)
 *                                e.g., [100, 200, 100] creates 4 columns with varying gaps
 * @param {number[]} yDistances - Array of distances in meters for Y direction (rows)
 *                                e.g., [50, 75, 50] creates 4 rows with varying gaps
 * @returns {number[][][]} - 2D array of coordinates: gridPoints[row][col] = [lon, lat]
 */
export function generateVariableGrid(
  topLeft: Coordinate,
  topRight: Coordinate,
  bottomLeft: Coordinate,
  bottomRight: Coordinate,
  xDistances: number[],
  yDistances: number[]
): number[][][] {
  const gridPoints: Coordinate[][] = [];

  // Calculate cumulative distances for Y (rows)
  const yCumulative = [0];
  for (const dist of yDistances) {
    yCumulative.push(yCumulative[yCumulative.length - 1] + dist);
  }

  // Calculate cumulative distances for X (columns)
  const xCumulative = [0];
  for (const dist of xDistances) {
    xCumulative.push(xCumulative[xCumulative.length - 1] + dist);
  }

  // Number of points = number of gaps + 1
  const rowCount = yDistances.length;
  const colCount = xDistances.length;

  for (let row = 0; row < rowCount; row++) {
    const rowPoints: Coordinate[] = [];
    const yMeters = yCumulative[row];

    // Get start and end points for this row (interpolate along left and right edges)
    const rowStart = getPointFromMeters(topLeft, bottomLeft, yMeters);
    const rowEnd = getPointFromMeters(topRight, bottomRight, yMeters);

    for (let col = 0; col < colCount; col++) {
      const xMeters = xCumulative[col];
      const point = getPointFromMeters(rowStart, rowEnd, xMeters);
      rowPoints.push(point);
    }

    gridPoints.push(rowPoints);
  }

  return gridPoints;
}

/**
 * Calculate distance in meters between two coordinates (flat earth approximation)
 */
export function getDistanceMeters(coord1: Coordinate, coord2: Coordinate) {
  const [lon1, lat1] = coord1;
  const [lon2, lat2] = coord2;

  const avgLat = (lat1 + lat2) / 2;
  const latRad = (avgLat * Math.PI) / 180;

  const metersPerDegreeLat = 111320;
  const metersPerDegreeLon = 111320 * Math.cos(latRad);

  const dx = (lon2 - lon1) * metersPerDegreeLon;
  const dy = (lat2 - lat1) * metersPerDegreeLat;

  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calculate corner positions from center point and grid dimensions.
 * Assumes grid is oriented north-south (can be rotated separately if needed).
 *
 * @param centerLat - Center latitude in decimal degrees
 * @param centerLon - Center longitude in decimal degrees
 * @param widthMeters - Total width of grid in meters (east-west)
 * @param heightMeters - Total height of grid in meters (north-south)
 * @param rotationDegrees - Optional rotation angle in degrees (0 = north, clockwise positive)
 * @returns Object with corner coordinates { NW, NE, SE, SW } in decimal degrees
 */
export function calculateCornersFromCenter(
  centerLon: number,
  centerLat: number,
  widthMeters: number,
  heightMeters: number,
  rotationDegrees = 0
): {
  NW: Coordinate;
  NE: Coordinate;
  SE: Coordinate;
  SW: Coordinate;
} {
  const earthRadius = 6378137; // Earth radius in meters

  // Half dimensions
  const halfWidth = widthMeters / 2;
  const halfHeight = heightMeters / 2;

  // Convert meters to degrees
  const deltaLat = (halfHeight / earthRadius) * (180 / Math.PI);
  const deltaLon = (halfWidth / (earthRadius * Math.cos(centerLat * (Math.PI / 180)))) * (180 / Math.PI);

  // Calculate unrotated corners
  const nw: Coordinate = [centerLon - deltaLon, centerLat + deltaLat];
  const ne: Coordinate = [centerLon + deltaLon, centerLat + deltaLat];
  const se: Coordinate = [centerLon + deltaLon, centerLat - deltaLat];
  const sw: Coordinate = [centerLon - deltaLon, centerLat - deltaLat];

  // If rotation is needed, rotate around center
  if (rotationDegrees !== 0) {
    const center: Coordinate = [centerLon, centerLat];
    return {
      NW: rotatePoint(nw, center, rotationDegrees),
      NE: rotatePoint(ne, center, rotationDegrees),
      SE: rotatePoint(se, center, rotationDegrees),
      SW: rotatePoint(sw, center, rotationDegrees),
    };
  }

  return { NW: nw, NE: ne, SE: se, SW: sw };
}
